import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { 
  BarChart3, 
  Plus, 
  Edit3, 
  Save,
  X,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Target,
  ArrowRight,
  Settings
} from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const ALLOCATION_METHODS = [
  { value: "percentage", label: "Percentage-Based" },
  { value: "headcount", label: "Headcount Driver" },
  { value: "payroll", label: "Payroll Driver" },
  { value: "revenue", label: "Revenue Driver" },
  { value: "square_footage", label: "Square Footage" },
  { value: "custom", label: "Custom Formula" }
];

const POOL_TEMPLATES = [
  {
    name: "Facilities Allocation",
    code: "FAC",
    method: "square_footage",
    description: "Allocate facilities costs based on square footage"
  },
  {
    name: "IT Services",
    code: "IT",
    method: "headcount",
    description: "Allocate IT costs based on headcount"
  },
  {
    name: "HR Allocations",
    code: "HR",
    method: "payroll",
    description: "Allocate HR costs based on payroll dollars"
  }
];

const IntermediateAllocations = ({ calculationId, onPoolsUpdate, onResultsUpdate }) => {
  const [intermediatePools, setIntermediatePools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state for adding/editing pools
  const [showForm, setShowForm] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [formData, setFormData] = useState({
    pool_name: "",
    pool_code: "",
    allocation_method: "percentage",
    total_costs: "",
    allocation_rules: {},
    nesting_level: 1,
    parent_pool_id: null
  });

  useEffect(() => {
    if (calculationId) {
      loadIntermediatePools();
    }
  }, [calculationId]);

  const loadIntermediatePools = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/intermediate-pools/${calculationId}`);
      const pools = response.data.intermediate_pools || [];
      setIntermediatePools(pools);
      
      if (onPoolsUpdate) {
        onPoolsUpdate(pools);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading intermediate pools");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePool = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!formData.pool_name.trim() || !formData.pool_code.trim()) {
      setError("Pool name and code are required");
      setSaving(false);
      return;
    }

    if (!formData.total_costs || parseFloat(formData.total_costs) <= 0) {
      setError("Please enter a valid total cost amount");
      setSaving(false);
      return;
    }

    // Check for duplicate pool codes
    const duplicateCode = intermediatePools.find(pool => 
      pool.pool_code === formData.pool_code && pool.id !== editingPool?.id
    );
    if (duplicateCode) {
      setError("Pool code must be unique");
      setSaving(false);
      return;
    }

    try {
      const poolData = {
        calculation_id: calculationId,
        pool_name: formData.pool_name.trim(),
        pool_code: formData.pool_code.trim().toUpperCase(),
        allocation_method: formData.allocation_method,
        total_costs: parseFloat(formData.total_costs),
        allocation_rules: formData.allocation_rules,
        nesting_level: formData.nesting_level,
        parent_pool_id: formData.parent_pool_id,
        created_by: "user"
      };

      const response = await axios.post(`${API}/intermediate-pools`, poolData);

      if (response.data.success) {
        setSuccess(editingPool ? "Intermediate pool updated successfully!" : "Intermediate pool created successfully!");
        resetForm();
        loadIntermediatePools(); // Reload the list
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Error saving intermediate pool");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPool = (pool) => {
    setEditingPool(pool);
    setFormData({
      pool_name: pool.pool_name || "",
      pool_code: pool.pool_code || "",
      allocation_method: pool.allocation_method || "percentage",
      total_costs: pool.total_costs ? pool.total_costs.toString() : "",
      allocation_rules: pool.allocation_rules || {},
      nesting_level: pool.nesting_level || 1,
      parent_pool_id: pool.parent_pool_id || null
    });
    setShowForm(true);
  };

  const handleApplyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      pool_name: template.name,
      pool_code: template.code,
      allocation_method: template.method
    }));
  };

  const resetForm = () => {
    setFormData({
      pool_name: "",
      pool_code: "",
      allocation_method: "percentage",
      total_costs: "",
      allocation_rules: {},
      nesting_level: 1,
      parent_pool_id: null
    });
    setEditingPool(null);
    setShowForm(false);
  };

  const renderAllocationRulesEditor = () => {
    if (formData.allocation_method === "percentage") {
      return (
        <div className="space-y-3">
          <Label>Allocation Rules (Percentage Distribution)</Label>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-sm font-medium">
              <span>Target Pool</span>
              <span>Percentage (%)</span>
              <span>Actions</span>
            </div>
            
            {Object.entries(formData.allocation_rules).map(([targetId, percentage], index) => (
              <div key={index} className="grid grid-cols-3 gap-2 items-center">
                <Input
                  placeholder="Pool ID or Name"
                  value={targetId}
                  onChange={(e) => {
                    const newRules = { ...formData.allocation_rules };
                    delete newRules[targetId];
                    newRules[e.target.value] = percentage;
                    setFormData(prev => ({ ...prev, allocation_rules: newRules }));
                  }}
                />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={percentage}
                  onChange={(e) => {
                    const newRules = { ...formData.allocation_rules };
                    newRules[targetId] = parseFloat(e.target.value) || 0;
                    setFormData(prev => ({ ...prev, allocation_rules: newRules }));
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const newRules = { ...formData.allocation_rules };
                    delete newRules[targetId];
                    setFormData(prev => ({ ...prev, allocation_rules: newRules }));
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const newRules = { ...formData.allocation_rules };
                newRules[`target_${Object.keys(newRules).length + 1}`] = 0;
                setFormData(prev => ({ ...prev, allocation_rules: newRules }));
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Target
            </Button>
            
            {/* Percentage Total Validation */}
            {Object.keys(formData.allocation_rules).length > 0 && (
              <div className="text-sm">
                <span className="font-medium">Total Percentage: </span>
                <span className={`${
                  Object.values(formData.allocation_rules).reduce((sum, val) => sum + (val || 0), 0) === 100 
                    ? "text-green-600" 
                    : "text-red-600"
                }`}>
                  {Object.values(formData.allocation_rules).reduce((sum, val) => sum + (val || 0), 0).toFixed(2)}%
                </span>
                {Object.values(formData.allocation_rules).reduce((sum, val) => sum + (val || 0), 0) !== 100 && (
                  <span className="text-red-600 ml-2">(must equal 100%)</span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="p-4 bg-slate-50 rounded-lg">
        <p className="text-sm text-slate-600">
          <Settings className="h-4 w-4 inline mr-1" />
          Advanced allocation rules for <strong>{formData.allocation_method}</strong> method will be configured automatically based on available drivers.
        </p>
      </div>
    );
  };

  const formatCurrency = (value) => {
    return value ? `$${value.toLocaleString()}` : 'N/A';
  };

  const getMethodLabel = (method) => {
    return ALLOCATION_METHODS.find(m => m.value === method)?.label || method;
  };

  const calculateTotalCosts = () => {
    return intermediatePools.reduce((sum, pool) => sum + (pool.total_costs || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading intermediate pools...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-red-700">
            {error}
          </AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-green-700">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Statistics */}
      {intermediatePools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-blue-600" />
              <span>Intermediate Allocation Summary</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-900">Total Pools</div>
                <div className="text-2xl font-bold text-blue-600">{intermediatePools.length}</div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-blue-900">Total Costs</div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(calculateTotalCosts())}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-green-900">Active Methods</div>
                <div className="text-2xl font-bold text-green-600">
                  {new Set(intermediatePools.map(pool => pool.allocation_method)).size}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Intermediate Pools Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              <span>Intermediate Allocation Pools</span>
            </CardTitle>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Pool
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {intermediatePools.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium mb-2 text-slate-600">No Intermediate Pools</h3>
              <p className="text-sm text-slate-500 mb-4">
                Create intermediate allocation pools to enable multi-tier cost distribution.
              </p>
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Pool
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pool Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Allocation Method</TableHead>
                    <TableHead className="text-right">Total Costs</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {intermediatePools.map((pool) => (
                    <TableRow key={pool.id}>
                      <TableCell className="font-medium">{pool.pool_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{pool.pool_code}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {getMethodLabel(pool.allocation_method)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <DollarSign className="h-3 w-3 text-slate-400" />
                          <span>{formatCurrency(pool.total_costs)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <span>Level {pool.nesting_level}</span>
                          {pool.nesting_level > 1 && (
                            <ArrowRight className="h-3 w-3 text-slate-400" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={pool.is_active ? "default" : "secondary"}
                          className={pool.is_active 
                            ? "bg-green-100 text-green-800" 
                            : "bg-gray-100 text-gray-600"
                          }
                        >
                          {pool.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPool(pool)}
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <span>{editingPool ? "Edit Intermediate Pool" : "Add Intermediate Pool"}</span>
            </h3>
            
            {/* Template Selection */}
            {!editingPool && (
              <div className="mb-6">
                <Label>Pool Templates (Optional)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                  {POOL_TEMPLATES.map((template, index) => (
                    <div 
                      key={index}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-slate-50"
                      onClick={() => handleApplyTemplate(template)}
                    >
                      <div className="font-medium">{template.name}</div>
                      <div className="text-sm text-slate-500">{template.description}</div>
                      <Badge variant="outline" className="mt-1">{template.code}</Badge>
                    </div>
                  ))}
                </div>
                <Separator className="mt-4" />
              </div>
            )}
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pool_name">Pool Name *</Label>
                  <Input
                    id="pool_name"
                    placeholder="e.g., Facilities Allocation"
                    value={formData.pool_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, pool_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="pool_code">Pool Code *</Label>
                  <Input
                    id="pool_code"
                    placeholder="e.g., FAC"
                    value={formData.pool_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, pool_code: e.target.value.toUpperCase() }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="allocation_method">Allocation Method</Label>
                  <Select 
                    value={formData.allocation_method} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, allocation_method: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALLOCATION_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="total_costs">Total Costs ($) *</Label>
                  <Input
                    id="total_costs"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="250000.00"
                    value={formData.total_costs}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_costs: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nesting_level">Nesting Level</Label>
                  <Select 
                    value={formData.nesting_level.toString()} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, nesting_level: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1 (Top Level)</SelectItem>
                      <SelectItem value="2">Level 2 (Nested)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.nesting_level > 1 && (
                  <div>
                    <Label htmlFor="parent_pool_id">Parent Pool</Label>
                    <Select 
                      value={formData.parent_pool_id || ""} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, parent_pool_id: value || null }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select parent pool" />
                      </SelectTrigger>
                      <SelectContent>
                        {intermediatePools.filter(pool => pool.nesting_level === 1).map(pool => (
                          <SelectItem key={pool.id} value={pool.id}>
                            {pool.pool_name} ({pool.pool_code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              {/* Allocation Rules Editor */}
              {renderAllocationRulesEditor()}
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setError(null);
                  setSuccess(null);
                }}
                disabled={saving}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSavePool}
                disabled={saving || !formData.pool_name.trim() || !formData.pool_code.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : (editingPool ? "Update Pool" : "Create Pool")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Information Panel */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-slate-600">
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-medium">Intermediate Pool Guidelines</p>
            <ul className="text-xs mt-1 space-y-1">
              <li>• Intermediate pools enable multi-tier cost allocation before final pool distribution</li>
              <li>• Percentage-based allocation requires rules that sum to exactly 100%</li>
              <li>• Level 2 (nested) pools allocate costs from Level 1 pools</li>
              <li>• Driver-based methods use business unit data for automatic allocation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntermediateAllocations;